import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2 } from "lucide-react";
import { SignaturePad } from "@/components/SignPage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public`;
const feld = "w-full rounded-lg border border-[#E0D6CA] bg-white px-3 py-3 text-base text-[#3D3835] focus:outline-none focus:ring-2 focus:ring-[#9A3B2E]/40";
const knopf = "w-full rounded-lg bg-[#9A3B2E] hover:bg-[#7F2F25] text-white font-semibold py-3 transition-colors disabled:opacity-50";

const Rahmen = ({ titel, children }) => (
  <div className="min-h-screen bg-[#F5F0EB] text-[#3D3835]" data-testid="erechnung-public">
    <header className="px-5 pt-10 pb-4 max-w-xl mx-auto">
      <p className="text-[11px] uppercase tracking-[0.25em] text-[#9A3B2E] font-semibold">Dickies-Campus</p>
      <h1 className="text-3xl sm:text-4xl font-serif mt-2 leading-tight">{titel}</h1>
    </header>
    <main className="px-5 pb-16 max-w-xl mx-auto"><div className="bg-white rounded-2xl p-5 shadow-sm">{children}</div></main>
    <footer className="text-center text-[11px] text-[#8E7B71] pb-8 px-5">
      Dickies-Campus GmbH · Neuenhausplatz 73, 40699 Erkrath ·{" "}
      <a className="underline" href="https://dickies-helden.de/datenschutz" target="_blank" rel="noreferrer">Datenschutz</a>
    </footer>
  </div>
);

const Fertig = ({ text }) => (
  <div className="text-center py-6" data-testid="erechnung-public-fertig">
    <CheckCircle2 size={44} className="mx-auto text-[#4F6650]" />
    <p className="mt-3 text-lg font-semibold">Vielen Dank!</p>
    <p className="text-sm text-[#8E7B71] mt-1">{text}</p>
  </div>
);

const useLaden = (pfad) => {
  const [d, setD] = useState(null);
  const [fehler, setFehler] = useState("");
  useEffect(() => { axios.get(`${API}/${pfad}`).then((r) => setD(r.data)).catch((e) => setFehler(e.response?.data?.detail || "Link ungültig")); }, [pfad]);
  return { d, fehler };
};

export function RechnungsadresseFormular() {
  const { token } = useParams();
  const { d, fehler } = useLaden(`rechnungsadresse/${token}`);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [ok, setOk] = useState(false);
  useEffect(() => { if (d?.rechnung_email) setEmail(d.rechnung_email); }, [d]);
  const senden = async (e) => {
    e.preventDefault(); setBusy(true); setMsg("");
    try { await axios.post(`${API}/rechnungsadresse/${token}`, { email, name }); setOk(true); }
    catch (err) { setMsg(err.response?.data?.detail || "Speichern fehlgeschlagen"); }
    setBusy(false);
  };
  return (
    <Rahmen titel="E-Mail-Adresse für Rechnungen">
      {fehler ? <p className="text-sm text-[#C2452F]">{fehler}</p> : !d ? <Loader2 className="animate-spin" /> : ok ? (
        <Fertig text={`Wir senden die Rechnungen für ${d.name} ab sofort als E-Rechnung an ${email}.`} />
      ) : (
        <form onSubmit={senden} className="space-y-4">
          <p className="text-sm">Für <strong>{d.name}</strong>: An welche E-Mail-Adresse sollen wir Rechnungen künftig senden? Die Adresse verwenden wir nur für Rechnungen.</p>
          <input className={feld} type="email" required placeholder="z. B. rechnung@ihre-einrichtung.de" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="rechnungsadresse-email" />
          <input className={feld} placeholder="Ihr Name (optional)" value={name} onChange={(e) => setName(e.target.value)} data-testid="rechnungsadresse-name" />
          {msg && <p className="text-sm text-[#C2452F]" data-testid="rechnungsadresse-fehler">{msg}</p>}
          <button className={knopf} disabled={busy} data-testid="rechnungsadresse-senden">{busy ? "Speichere…" : "Adresse speichern"}</button>
        </form>
      )}
    </Rahmen>
  );
}

export function EinwilligungRechnung() {
  const { token } = useParams();
  const { d, fehler } = useLaden(`einwilligung-rechnung/${token}`);
  const [haken, setHaken] = useState(false);
  const [busy, setBusy] = useState(false);
  const [ergebnis, setErgebnis] = useState(null);
  const senden = async (zustimmung) => {
    setBusy(true);
    try { const r = await axios.post(`${API}/einwilligung-rechnung/${token}`, { zustimmung }); setErgebnis(r.data.status); } catch { setErgebnis("fehler"); }
    setBusy(false);
  };
  return (
    <Rahmen titel="Rechnungen per E-Mail?">
      {fehler ? <p className="text-sm text-[#C2452F]">{fehler}</p> : !d ? <Loader2 className="animate-spin" /> : ergebnis === "ja" ? (
        <Fertig text={`Deine Rechnungen kommen ab jetzt per E-Mail an ${d.email}.`} />
      ) : ergebnis === "nein" ? (
        <Fertig text="Alles klar – wir schicken dir deine Rechnungen weiterhin per Post." />
      ) : (
        <div className="space-y-4">
          <p className="text-sm">Hallo {d.vorname}, dürfen wir dir deine Rechnungen per E-Mail an <strong>{d.email}</strong> schicken?</p>
          {d.status && <p className="text-xs text-[#8E7B71]" data-testid="einwilligung-aktuell">Aktuell gespeichert: {d.status === "ja" ? "Rechnungen per E-Mail" : "Rechnungen per Post"} – du kannst das hier ändern.</p>}
          <label className="flex gap-3 items-start text-sm bg-[#F5F0EB] rounded-lg p-3 cursor-pointer">
            <input type="checkbox" className="mt-1 h-5 w-5 accent-[#9A3B2E]" checked={haken} onChange={(e) => setHaken(e.target.checked)} data-testid="einwilligung-haken" />
            <span>{d.text}</span>
          </label>
          {ergebnis === "fehler" && <p className="text-sm text-[#C2452F]">Speichern fehlgeschlagen – bitte nochmal versuchen.</p>}
          <button className={knopf} disabled={!haken || busy} onClick={() => senden(true)} data-testid="einwilligung-zustimmen">Ja, per E-Mail</button>
          <button className="w-full text-sm text-[#8E7B71] underline py-2" disabled={busy} onClick={() => senden(false)} data-testid="einwilligung-ablehnen">Nein, lieber per Post</button>
        </div>
      )}
    </Rahmen>
  );
}


const Haken = ({ checked, onChange, children, testid }) => (
  <label className="flex gap-3 items-start text-sm bg-[#F5F0EB] rounded-lg p-3 cursor-pointer">
    <input type="checkbox" className="mt-0.5 h-5 w-5 shrink-0 accent-[#9A3B2E]" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid} />
    <span>{children}</span>
  </label>
);

export function EinwilligungEinrichtung() {
  const { token } = useParams();
  const { d, fehler } = useLaden(`einwilligung-einrichtung/${token}`);
  const [werbung, setWerbung] = useState({ email: false, telefon: false, whatsapp: false });
  const [ds, setDs] = useState(false);
  const [name, setName] = useState("");
  const [funktion, setFunktion] = useState("");
  const [sig, setSig] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [ok, setOk] = useState(false);
  useEffect(() => {
    if (d?.aktuell) { setWerbung({ email: false, telefon: false, whatsapp: false, ...d.aktuell.werbung }); setDs(!!d.aktuell.datenschutz); }
  }, [d]);
  const senden = async (e) => {
    e.preventDefault(); setBusy(true); setMsg("");
    try { await axios.post(`${API}/einwilligung-einrichtung/${token}`, { werbung, datenschutz: ds, name, funktion, unterschrift: sig }); setOk(true); }
    catch (err) { setMsg(err.response?.data?.detail || "Speichern fehlgeschlagen"); }
    setBusy(false);
  };
  return (
    <Rahmen titel="Einwilligung Kontakt & Datenschutz">
      {fehler ? <p className="text-sm text-[#C2452F]">{fehler}</p> : !d ? <Loader2 className="animate-spin" /> : ok ? (
        <Fertig text={`Vielen Dank – Ihre Angaben für ${d.name} sind gespeichert. Sie können sie jederzeit widerrufen.`} />
      ) : (
        <form onSubmit={senden} className="space-y-4" data-testid="einwilligung-einrichtung-form">
          <p className="text-sm">Für <strong>{d.name}</strong>. Bitte kreuzen Sie an, was für Sie passt – jeder Punkt ist freiwillig.</p>
          {d.aktuell && <p className="text-xs text-[#8E7B71]" data-testid="ew-aktuell">Ihre bisherigen Angaben sind vorausgewählt – Sie können sie hier ändern.</p>}
          <p className="text-sm font-semibold">1. Kontakt zu Angeboten</p>
          <p className="text-sm text-[#5C534D]">{d.werbe_text}</p>
          {Object.entries(d.kanaele).map(([k, l]) => (
            <Haken key={k} checked={werbung[k]} onChange={(v) => setWerbung({ ...werbung, [k]: v })} testid={`ew-kanal-${k}`}>{l}</Haken>
          ))}
          <p className="text-sm font-semibold pt-2">2. Datenschutz</p>
          <Haken checked={ds} onChange={setDs} testid="ew-datenschutz">{d.ds_text}</Haken>
          <p className="text-xs text-[#8E7B71]">{d.widerruf_text}</p>
          <input className={feld} required placeholder="Ihr Name" value={name} onChange={(e) => setName(e.target.value)} data-testid="ew-name" />
          <input className={feld} required placeholder="Ihre Funktion (z. B. Pflegedienstleitung)" value={funktion} onChange={(e) => setFunktion(e.target.value)} data-testid="ew-funktion" />
          <SignaturePad onSignatureChange={setSig} />
          {msg && <p className="text-sm text-[#C2452F]" data-testid="ew-fehler">{msg}</p>}
          <button className={knopf} disabled={busy || !sig || !name || !funktion} data-testid="ew-absenden">{busy ? "Speichere…" : "Unterschreiben und absenden"}</button>
        </form>
      )}
    </Rahmen>
  );
}
